/**
 * Session Booked Notification Component
 * Displays a notification when someone books a session (sent to session creator)
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface SessionBookedNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface SessionBookedNotificationData {
  bookingId?: string
  sessionId?: string
  sessionTitle?: string
  bookerId?: string
  bookerName?: string
  bookerUsername?: string | null
  sessionCoverImageUrl?: string | null
  date?: string
  startTime?: string
  isPaid?: boolean
  fee?: number | null
}

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

export function SessionBookedNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: SessionBookedNotificationProps) {
  const data = notification.data as SessionBookedNotificationData

  const sessionTitle = data.sessionTitle || "a session"
  const bookerUsername = data.bookerUsername
  const sessionCoverImageUrl = data.sessionCoverImageUrl
  const date = data.date
  const startTime = data.startTime
  const isPaid = data.isPaid
  const fee = data.fee
  const isRead = notification.isRead

  const booker = notification.triggeredByUser
  const displayImageUrl = getUserDisplayImage(booker)
  const displayName = booker?.name || booker?.username || data.bookerName || "Someone"

  const relativeTime = formatNotificationTime(notification.createdAt)

  const priceDisplay = isPaid && fee ? `$${fee}` : "Free"

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  const formatDateTime = (dateStr?: string, timeStr?: string): string => {
    if (!dateStr) return ""
    const parts = [dateStr]
    if (timeStr) parts.push(timeStr)
    return parts.join(" ")
  }

  const dateTimeDisplay = formatDateTime(date, startTime)

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md transition-colors border cursor-pointer ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Avatar, Name, Username, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Booker avatar */}
          <div className="w-12 h-12 rounded-full overflow-hidden bg-card flex-shrink-0">
            {displayImageUrl ? (
              <img src={displayImageUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-lg">
                {displayName[0]}
              </div>
            )}
          </div>

          {/* Name, Username, Time */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{displayName}</span>
              {bookerUsername && <span className="text-stone text-[14px]">@{bookerUsername}</span>}
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              <span className="font-semibold">{displayName}</span> booked your session{" "}
              <span className="font-medium">{sessionTitle}</span>
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-1 -right-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Session Card */}
      <div className="relative mt-2 h-[357.5px] mx-auto md:w-[520px] rounded-2xl overflow-hidden border border-card">
        {sessionCoverImageUrl ? (
          <img
            src={sessionCoverImageUrl}
            alt={sessionTitle}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-card flex items-center justify-center">
            <span className="text-stone text-sm">No cover image</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-[20px] bg-gradient-to-t from-[rgba(0, 0, 0, 0.83)] to-transparent">
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[20px] text-white truncate leading-[20px]">
              {sessionTitle}
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-white text-[13px] px-2 py-1 rounded-sm leading-[20px] ${isPaid ? "bg-brand-terracotta" : "bg-brand-olive"}`}
              >
                {priceDisplay}
              </span>
              {dateTimeDisplay && (
                <span className="text-white/80 text-[13px] leading-[20px]">{dateTimeDisplay}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
